import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Library as LibraryIcon, MapPin, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { BookCardItem, type BookCard } from "@/components/BookCard";
import { getStoredLocation, type Coords } from "@/lib/location";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

type Library = {
  id: string;
  name: string;
  description: string | null;
  address: string | null;
  cover_url: string | null;
};

export const Route = createFileRoute("/libraries/$id")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase.rpc("get_public_libraries");
      const lib = (data ?? []).find((l: any) => l.id === params.id) ?? null;
      return { lib };
    } catch {
      return { lib: null };
    }
  },
  head: ({ params, loaderData }) => {
    const lib = loaderData?.lib as { name: string; description: string | null; address: string | null } | null | undefined;
    const title = lib ? `${lib.name} — community library on Book Swap` : "Community library — Book Swap";
    const description = lib
      ? (lib.description?.trim() ||
          `${lib.name} is a neighborhood library on Book Swap${lib.address ? ` in ${lib.address}` : ""}. See the books on its shelves and take or leave one.`
        ).slice(0, 160)
      : "See the books on this neighborhood library's shelves and arrange to take or leave one.";
    const head = pageHead({ path: `/libraries/${params.id}`, title, description });
    return {
      ...head,
      scripts: lib
        ? [
            jsonLd({
              "@context": "https://schema.org",
              "@type": "Place",
              name: lib.name,
              description: lib.description ?? undefined,
              address: lib.address ?? undefined,
              url: `${SITE_URL}/libraries/${params.id}`,
            }),
          ]
        : [],
    };
  },
  component: LibraryDetail,
});

function LibraryDetail() {
  const { id } = Route.useParams();
  const [lib, setLib] = useState<Library | null>(null);
  const [books, setBooks] = useState<BookCard[]>([]);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCoords(getStoredLocation());
    Promise.all([
      supabase.rpc("get_public_libraries" as never),
      supabase.from("books").select("id,title,author,cover_url,category,age_group,exchange_duration,condition,lat,lng,status").eq("library_id", id).order("created_at", { ascending: false }),
    ]).then(([{ data: lData }, { data: bData }]) => {
      const found = ((lData ?? []) as Library[]).find((l) => l.id === id) ?? null;
      setLib(found);
      setBooks((bData as BookCard[]) ?? []);
      setLoading(false);
    });
  }, [id]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><p className="mt-12 text-center text-muted-foreground">Loading…</p></div>;
  if (!lib) return <div className="min-h-screen bg-background"><Navbar /><p className="mt-12 text-center text-muted-foreground">Library not found.</p></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Link to="/libraries" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> All libraries
        </Link>
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-3xl">🏛️</div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{lib.name}</h1>
            {lib.address && (
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" /> {lib.address}
              </p>
            )}
          </div>
        </div>
        {lib.description && <p className="mt-4 text-sm">{lib.description}</p>}

        <h2 className="mt-10 flex items-center gap-2 text-xl font-semibold"><LibraryIcon className="h-5 w-5 text-primary" /> Books at this library</h2>
        {books.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No books listed at this library yet.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {books.map((b) => <BookCardItem key={b.id} book={b} here={coords} />)}
          </div>
        )}
      </div>
    </div>
  );
}
